import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getActiveOrgId,
  getCertificateStatus,
  getToken,
  type CertificateStatusResponse,
} from '../services/api';
import { type NavView } from '../components/Layout';
import { useT } from '../i18n';
// Issue #550: Page shell — remove wrapper proprio (max-w-4xl p-6
// duplicava o <main> do Layout) e padroniza o header da pagina.
import Page from '../components/Page';

// --- API helpers (local, minimal) ---

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface SearchResult {
  chave_acesso: string;
  status: string;
  error: string | null;
  document_id: string | null;
  import_id?: string;
}

interface BatchInfo {
  batch_id: string;
  status: string;
  total: number;
  processed: number;
  success: number;
  error: number;
  document_ids: string[];
  errors: { chave: string; error: string }[];
  created_at: string | null;
  completed_at: string | null;
}

async function searchByKey(chave: string): Promise<SearchResult> {
  const res = await fetch('/api/sefaz/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ chave_acesso: chave }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.detail || 'Erro na busca');
  }
  return data as SearchResult;
}

async function fetchBatchStatus(batchId: string): Promise<BatchInfo> {
  const res = await fetch(`/api/sefaz/batch/${batchId}`, { headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Erro ao buscar status');
  return data as BatchInfo;
}

async function fetchBatches(): Promise<BatchInfo[]> {
  const res = await fetch('/api/sefaz/batches', { headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Erro ao listar lotes');
  return data.batches as BatchInfo[];
}

async function uploadBatchExcel(file: File): Promise<{ batch_id: string; total: number; invalid: number }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch('/api/sefaz/batch', {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Erro no upload');
  return data;
}

function downloadTemplate() {
  fetch('/api/sefaz/template', { headers: authHeaders() })
    .then(res => res.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_sefaz.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    });
}

// Issue #507: exporta o resultado do lote (.xlsx ou .csv).
// Checa res.ok ANTES de baixar — sem isso, um 404/422 salvaria o JSON de
// erro como se fosse o arquivo exportado.
async function exportBatch(batchId: string, format: 'xlsx' | 'csv'): Promise<void> {
  const res = await fetch(`/api/sefaz/batch/${batchId}/export?format=${format}`, { headers: authHeaders() });
  if (!res.ok) {
    let detail = 'Erro ao exportar lote';
    try {
      const data = await res.json();
      if (typeof data?.detail === 'string') detail = data.detail;
      else if (data?.detail?.error) detail = data.detail.error;
    } catch { /* corpo nao-JSON — mantem a mensagem padrao */ }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sefaz_batch_${batchId}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

// --- Chave de acesso validation (44 digits + mod 11) ---
function validateChave(chave: string): boolean {
  const digits = chave.replace(/\D/g, '');
  if (digits.length !== 44) return false;
  let soma = 0;
  let peso = 2;
  for (let i = 42; i >= 0; i--) {
    soma += parseInt(digits[i], 10) * peso;
    peso++;
    if (peso > 9) peso = 2;
  }
  const resto = soma % 11;
  let dv = 11 - resto;
  if (dv >= 10) dv = 0;
  return dv === parseInt(digits[43], 10);
}

// --- Mask: groups of 4 digits ---
function maskChave(chave: string): string {
  const d = chave.replace(/\D/g, '');
  return d.match(/.{1,4}/g)?.join(' ') ?? '';
}

// Issue #475: a consulta por chave exige certificado A1 configurado no MCP
// (tool autenticada). Enquanto nao ha endpoint de status de certificado,
// detectamos pelo erro do backend (erro_sessao/erro_tenant, codigos
// SESSION_*/NO_CERTIFICATE/cert) e exibimos o empty-state gated.
function isCertGateError(msg: string | null | undefined): boolean {
  return !!msg && /sess[aã]o|session|cert|tenant|credential|sem_certificado/i.test(msg);
}

function SefazSearch({ onNavigate }: { onNavigate?: (v: NavView) => void }) {
  const t = useT();
  const [tab, setTab] = useState<'individual' | 'batch'>('individual');
  const [certGate, setCertGate] = useState(false);
  // Issue #482 (S14-T08): badge proativo do status do cert custodiado —
  // GET /api/certificates/status e member-safe (metadados minimos).
  const [certStatus, setCertStatus] = useState<CertificateStatusResponse | null>(null);

  // --- Individual search ---
  const [chaveInput, setChaveInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const chaveDigits = chaveInput.replace(/\D/g, '');
  const chaveValid = chaveDigits.length === 44 && validateChave(chaveDigits);

  const handleSearch = useCallback(async () => {
    if (!chaveValid) return;
    setSearching(true);
    setSearchError(null);
    setSearchResult(null);
    try {
      const result = await searchByKey(chaveDigits);
      setSearchResult(result);
      // Issue #475/#482: erro de sessao/certificado ou sem_certificado ->
      // empty-state gated.
      if (
        result.status === 'erro_sessao' ||
        result.status === 'erro_tenant' ||
        result.status === 'sem_certificado' ||
        isCertGateError(result.error)
      ) {
        setCertGate(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      setSearchError(msg);
      if (isCertGateError(msg)) setCertGate(true);
    } finally {
      setSearching(false);
    }
  }, [chaveValid, chaveDigits]);

  // --- Batch ---
  const [uploading, setUploading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [activeBatch, setActiveBatch] = useState<BatchInfo | null>(null);
  const [batchList, setBatchList] = useState<BatchInfo[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadBatches = useCallback(async () => {
    try {
      const list = await fetchBatches();
      setBatchList(list);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // Status funcional do cert (member-safe) — badge proativo + gate imediato
  // quando a org nao tem cert custodiado (evita a 1a busca com erro).
  useEffect(() => {
    // Fix auditoria S14 (HIGH-1): envia X-Organization-ID da org ativa.
    getCertificateStatus(getActiveOrgId())
      .then((s) => {
        setCertStatus(s);
        if (!s.has_certificate) setCertGate(true);
      })
      .catch(() => setCertStatus(null));
  }, []);

  // Polling for active batch status
  useEffect(() => {
    if (activeBatch && (activeBatch.status === 'pending' || activeBatch.status === 'processing')) {
      pollRef.current = setInterval(async () => {
        try {
          const updated = await fetchBatchStatus(activeBatch.batch_id);
          setActiveBatch(updated);
          if (updated.status === 'completed' || updated.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current);
            loadBatches();
          }
        } catch { /* ignore */ }
      }, 5000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, [activeBatch, loadBatches]);

  const handleUpload = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setBatchError(t('sefaz.batch.errorFormat'));
      return;
    }
    setUploading(true);
    setBatchError(null);
    try {
      const result = await uploadBatchExcel(file);
      const info = await fetchBatchStatus(result.batch_id);
      setActiveBatch(info);
      loadBatches();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      setBatchError(msg);
      if (isCertGateError(msg)) setCertGate(true);
    } finally {
      setUploading(false);
    }
  }, [t, loadBatches]);

  // Issue #507: erro do export segue o mesmo padrao da pagina (batchError).
  const handleExport = useCallback((format: 'xlsx' | 'csv') => {
    if (!activeBatch) return;
    setBatchError(null);
    exportBatch(activeBatch.batch_id, format).catch((err) => {
      setBatchError(err instanceof Error ? err.message : 'Erro desconhecido');
    });
  }, [activeBatch]);

  return (
    <Page
      title={t('sefaz.title')}
      actions={
        certStatus ? (
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
              !certStatus.has_certificate || certStatus.status === 'expirado'
                ? 'bg-red-100 text-red-700'
                : certStatus.status === 'vencendo'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-green-100 text-green-700'
            }`}
          >
            {!certStatus.has_certificate
              ? t('sefaz.certs.status.ausente')
              : certStatus.status === 'vencendo'
                ? t('sefaz.certs.status.vencendo', { n: certStatus.days_until_expiry ?? '?' })
                : t(`sefaz.certs.status.${certStatus.status === 'pendente_consentimento' ? 'pendente' : certStatus.status}`)}
          </span>
        ) : undefined
      }
    >
      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-brand-200">
        <button
          className={`px-4 py-2 font-medium ${tab === 'individual' ? 'border-b-2 border-brand-600 text-brand-700' : 'text-brand-400'}`}
          onClick={() => setTab('individual')}
        >
          {t('sefaz.tab.individual')}
        </button>
        <button
          className={`px-4 py-2 font-medium ${tab === 'batch' ? 'border-b-2 border-brand-600 text-brand-700' : 'text-brand-400'}`}
          onClick={() => setTab('batch')}
        >
          {t('sefaz.tab.batch')}
        </button>
      </div>

      {/* Issue #475: empty-state gated — consulta requer certificado A1. */}
      {certGate && (
        <div className="mb-6 rounded-lg border border-brand-200 bg-brand-50 p-6 text-center">
          <p className="mb-2 font-semibold text-brand-800">{t('sefaz.gated.title')}</p>
          <p className="mb-4 text-sm text-brand-600">{t('sefaz.gated.body')}</p>
          <button
            onClick={() => onNavigate?.('sefaz-certs')}
            className="rounded-lg border border-brand-300 px-4 py-2 text-sm font-medium text-brand-600 hover:bg-white"
          >
            {t('sefaz.gated.cta')}
          </button>
        </div>
      )}

      {tab === 'individual' && (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-brand-600">{t('sefaz.individual.label')}</label>
            <input
              type="text"
              value={chaveInput}
              onChange={(e) => setChaveInput(maskChave(e.target.value))}
              placeholder="0000 0000 0000 0000 0000 0000 0000 0000 0000 0000 0000"
              className={`w-full rounded-lg border px-4 py-2 font-mono text-sm ${chaveDigits.length === 0 ? 'border-brand-200' : chaveValid ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'}`}
              maxLength={54}
            />
            {chaveDigits.length > 0 && !chaveValid && (
              <p className="mt-1 text-sm text-red-600">{t('sefaz.individual.invalid')}</p>
            )}
            {chaveValid && (
              <p className="mt-1 text-sm text-green-600">{t('sefaz.individual.valid')}</p>
            )}
          </div>

          <button
            onClick={handleSearch}
            disabled={!chaveValid || searching}
            className="rounded-lg bg-brand-600 px-6 py-2 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {searching ? t('common.processing') : t('sefaz.individual.search')}
          </button>

          {searchError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {searchError}
            </div>
          )}

          {searchResult && (
            <div className="rounded-lg border border-brand-200 bg-brand-50 p-4">
              <p className="mb-2 font-medium text-brand-700">{t('sefaz.individual.result')}</p>
              <dl className="space-y-1 text-sm">
                <div><dt className="inline font-medium">{t('sefaz.status')}: </dt><dd className="inline">{searchResult.status}</dd></div>
                {searchResult.error && <div><dt className="inline font-medium">{t('common.error')}: </dt><dd className="inline">{searchResult.error}</dd></div>}
                {searchResult.document_id && (
                  <>
                    <div><dt className="inline font-medium">Document ID: </dt><dd className="inline">{searchResult.document_id}</dd></div>
                    {searchResult.import_id && <div><dt className="inline font-medium">Import ID: </dt><dd className="inline">{searchResult.import_id}</dd></div>}
                    <button onClick={() => onNavigate?.('documents')} className="mt-2 text-sm text-brand-600 underline">{t('sefaz.individual.goToDocs')}</button>
                  </>
                )}
              </dl>
            </div>
          )}
        </div>
      )}

      {tab === 'batch' && (
        <div className="space-y-4">
          <button onClick={downloadTemplate} className="rounded-lg border border-brand-300 px-4 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50">
            {t('sefaz.batch.downloadTemplate')}
          </button>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center ${dragging ? 'border-brand-500 bg-brand-50' : 'border-brand-200'}`}
          >
            <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); }} />
            <p className="text-brand-500">{uploading ? t('common.processing') : t('sefaz.batch.dropHere')}</p>
          </div>

          {batchError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{batchError}</div>
          )}

          {activeBatch && (
            <div className="rounded-lg border border-brand-200 bg-brand-50 p-4">
              <p className="mb-3 font-medium text-brand-700">{t('sefaz.batch.progress')}</p>
              <div className="mb-2 flex gap-4 text-sm">
                <span>{t('sefaz.batch.total')}: {activeBatch.total}</span>
                <span>{t('sefaz.batch.processed')}: {activeBatch.processed}</span>
                <span className="text-green-600">{t('sefaz.batch.success')}: {activeBatch.success}</span>
                <span className="text-red-600">{t('sefaz.batch.error')}: {activeBatch.error}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-brand-100">
                <div className="h-full bg-brand-500 transition-all" style={{ width: `${activeBatch.total > 0 ? (activeBatch.processed / activeBatch.total) * 100 : 0}%` }} />
              </div>
              <p className="mt-2 text-sm text-brand-500">{t('sefaz.batch.status')}: {activeBatch.status}</p>
              {activeBatch.document_ids.length > 0 && (
                <button onClick={() => onNavigate?.('documents')} className="mt-2 text-sm text-brand-600 underline">
                  {t('sefaz.batch.goToDocs')} ({activeBatch.document_ids.length})
                </button>
              )}
              {(activeBatch.status === 'completed' || activeBatch.status === 'failed') && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="rounded-lg border border-brand-300 px-3 py-1.5 text-sm font-medium text-brand-600 hover:bg-white"
                  >
                    {t('sefaz.batch.export')} (.xlsx)
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="rounded-lg border border-brand-300 px-3 py-1.5 text-sm font-medium text-brand-600 hover:bg-white"
                  >
                    {t('sefaz.batch.export')} (.csv)
                  </button>
                </div>
              )}
            </div>
          )}

          {batchList.length > 0 && (
            <div>
              <p className="mb-2 font-medium text-brand-700">{t('sefaz.batch.history')}</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-brand-200 text-left text-brand-500">
                    <th className="py-2">{t('sefaz.batch.status')}</th>
                    <th className="py-2">{t('sefaz.batch.total')}</th>
                    <th className="py-2">{t('sefaz.batch.success')}</th>
                    <th className="py-2">{t('sefaz.batch.error')}</th>
                  </tr>
                </thead>
                <tbody>
                  {batchList.map((b) => (
                    <tr key={b.batch_id} className="border-b border-brand-100">
                      <td className="py-2">{b.status}</td>
                      <td className="py-2">{b.total}</td>
                      <td className="py-2 text-green-600">{b.success}</td>
                      <td className="py-2 text-red-600">{b.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}

export default SefazSearch;
